import React from "react";
import { clearAppStorage, getItem } from "utils/safeStorage";
import {
  clearStaleBuildFlag,
  isStaleBuildError,
  recoverFromStaleBuild,
} from "utils/staleBuildRecovery";

const COPY = {
  tr: {
    title: "Bir şeyler ters gitti",
    description:
      "Sayfa yüklenirken beklenmeyen bir hata oluştu. Yenilemek çoğu durumda sorunu çözer.",
    reload: "Sayfayı Yenile",
    reset: "Verileri temizle ve yeniden dene",
    details: "Teknik detay",
  },
  en: {
    title: "Something went wrong",
    description:
      "An unexpected error occurred while loading the page. Reloading usually fixes it.",
    reload: "Reload page",
    reset: "Clear data and try again",
    details: "Technical details",
  },
};

const getCopy = () => (getItem("locale") === "en" ? COPY.en : COPY.tr);

/**
 * Uygulama kökünde tek bir hata sınırı. Bu olmadan herhangi bir render hatası
 * (veya deploy sonrası bayat bundle nedeniyle patlayan `React.lazy` import'u)
 * tüm React ağacını unmount ederek kullanıcıya beyaz ekran gösteriyor.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { error, recovering: isStaleBuildError(error) };
  }

  componentDidMount() {
    // Başarılı bir mount, önceki kurtarma reload'unun işini bitirdiğini gösterir.
    clearStaleBuildFlag();
  }

  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary]", error, errorInfo);

    if (isStaleBuildError(error)) {
      const reloading = recoverFromStaleBuild();
      if (!reloading) {
        this.setState({ recovering: false });
      }
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    clearAppStorage();
    window.location.replace("/");
  };

  render() {
    const { error, recovering } = this.state;

    if (!error) {
      return this.props.children;
    }

    // Cache-bust reload yolda; araya ekran koymaya gerek yok.
    if (recovering) {
      return (
        <div className="flex h-screen w-full items-center justify-center bg-[#EFEFEF]">
          <div
            className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500"
            role="status"
            aria-label="Loading"
          />
        </div>
      );
    }

    const copy = getCopy();

    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-[#EFEFEF] p-5">
        <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow">
          <h4 className="mb-2 text-lg font-semibold text-gray-800">
            {copy.title}
          </h4>
          <p className="mb-6 text-sm text-gray-600">{copy.description}</p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="w-full rounded-md bg-gray-800 px-4 py-2.5 text-sm font-medium text-white"
            >
              {copy.reload}
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              className="w-full rounded-md border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700"
            >
              {copy.reset}
            </button>
          </div>
          {process.env.NODE_ENV !== "production" && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-xs text-gray-500">
                {copy.details}
              </summary>
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] text-gray-500">
                {String(error?.stack || error?.message || error)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
